import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowUpRight, Sparkles, Star, Clock, CornerDownLeft } from 'lucide-react';
import { useApp } from '@/store/app';
import { searchTools, routeIntent } from '@/lib/intent';
import { TOOLS } from '@/registry';
import { CATEGORY_BY_ID } from '@/registry/categories';
import { cn } from '@/lib/cn';
import type { Tool } from '@/registry/types';

const PROMPTS = [
  'merge these PDFs',
  'remove duplicates from CSV',
  'resize image for LinkedIn',
  'generate a strong password',
  'extract tables from a report',
  'check WCAG contrast ratio',
  'convert 50 USD to INR',
  'pretty print this JSON',
];

export function CommandPalette() {
  const { paletteOpen, setPaletteOpen, recents, favorites } = useApp();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [phIdx, setPhIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Rotating placeholder
  useEffect(() => {
    if (!paletteOpen || query) return;
    const t = setInterval(() => setPhIdx((i) => (i + 1) % PROMPTS.length), 2300);
    return () => clearInterval(t);
  }, [paletteOpen, query]);

  // Reset
  useEffect(() => {
    if (paletteOpen) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [paletteOpen]);

  const recentTools = useMemo(
    () =>
      recents
        .map((r) => TOOLS.find((t) => t.id === r.toolId))
        .filter((t): t is Tool => Boolean(t))
        .slice(0, 4),
    [recents],
  );

  const favTools = useMemo(
    () =>
      favorites
        .map((id) => TOOLS.find((t) => t.id === id))
        .filter((t): t is Tool => Boolean(t)),
    [favorites],
  );

  const intent = useMemo(() => (query ? routeIntent(query) : null), [query]);
  const results = useMemo(() => (query ? searchTools(query, 12) : []), [query]);

  // Sections shown when no query
  const idleSections = [
    { title: 'Recent', icon: Clock, items: recentTools },
    { title: 'Favorites', icon: Star, items: favTools },
    {
      title: 'Featured utilities',
      icon: Sparkles,
      items: TOOLS.filter((t) => t.featured).slice(0, 6),
    },
  ].filter((s) => s.items.length > 0);

  const flatItems = query ? results : idleSections.flatMap((s) => s.items);

  // Keyboard nav
  useEffect(() => {
    if (!paletteOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setPaletteOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => Math.min(flatItems.length - 1, a + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const tool = flatItems[active];
        if (tool) {
          navigate(`/tool/${tool.id}`);
          setPaletteOpen(false);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteOpen, flatItems, active, navigate, setPaletteOpen]);

  return (
    <AnimatePresence>
      {paletteOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setPaletteOpen(false)}
          />
          {/* Shell */}
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-[15vh] -translate-x-1/2 w-[min(640px,calc(100vw-2rem))] z-50"
          >
            <div className="bg-bg-elev/95 backdrop-blur-2xl border border-border-strong rounded-2xl shadow-2xl overflow-hidden">
              {/* Input */}
              <div className="flex items-center gap-3 px-4 h-14 border-b border-border">
                <Search size={16} className="text-fg-subtle shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  placeholder={`Try: "${PROMPTS[phIdx]}"`}
                  className="flex-1 h-full bg-transparent border-0 outline-none text-base placeholder:text-fg-subtle"
                />
                <kbd>esc</kbd>
              </div>

              {/* NL hint */}
              {query && intent?.toolId && (
                <div className="flex items-center gap-2 px-4 py-2 text-xs bg-accent-soft/40 border-b border-border">
                  <Sparkles size={12} className="text-accent" />
                  <span className="text-fg-muted">{intent.reason}</span>
                </div>
              )}

              {/* Results */}
              <div className="max-h-[55vh] overflow-y-auto p-2">
                {query ? (
                  results.length === 0 ? (
                    <div className="px-4 py-12 text-center text-sm text-fg-subtle">
                      No utility matches "{query}".
                      <div className="text-2xs mt-2">Try a different phrasing or describe the file you have.</div>
                    </div>
                  ) : (
                    results.map((tool, i) => (
                      <ResultRow
                        key={tool.id}
                        tool={tool}
                        active={i === active}
                        onSelect={() => {
                          navigate(`/tool/${tool.id}`);
                          setPaletteOpen(false);
                        }}
                        onMouseEnter={() => setActive(i)}
                      />
                    ))
                  )
                ) : (
                  <div className="space-y-3 py-1">
                    {idleSections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <div key={section.title}>
                          <div className="flex items-center gap-1.5 px-2.5 mt-1 mb-1 text-2xs font-medium uppercase tracking-wider text-fg-subtle">
                            <Icon size={10} />
                            {section.title}
                          </div>
                          {section.items.map((tool, i) => {
                            const idx =
                              idleSections
                                .slice(0, idleSections.indexOf(section))
                                .reduce((sum, s) => sum + s.items.length, 0) + i;
                            return (
                              <ResultRow
                                key={tool.id}
                                tool={tool}
                                active={idx === active}
                                onSelect={() => {
                                  navigate(`/tool/${tool.id}`);
                                  setPaletteOpen(false);
                                }}
                                onMouseEnter={() => setActive(idx)}
                              />
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 px-4 h-9 border-t border-border bg-bg-subtle text-2xs text-fg-subtle">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <kbd>↑</kbd>
                    <kbd>↓</kbd> navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <CornerDownLeft size={10} />
                    open
                  </span>
                </div>
                <div>
                  Powered by <span className="text-accent font-medium">intent routing</span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ResultRow({
  tool,
  active,
  onSelect,
  onMouseEnter,
}: {
  tool: Tool;
  active: boolean;
  onSelect: () => void;
  onMouseEnter: () => void;
}) {
  const Icon = tool.icon;
  const cat = CATEGORY_BY_ID[tool.category];
  return (
    <button
      onMouseEnter={onMouseEnter}
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors',
        active ? 'bg-accent-soft text-fg' : 'hover:bg-surface-hover',
      )}
    >
      <span
        className={cn(
          'size-8 rounded-md flex items-center justify-center bg-gradient-to-br shrink-0',
          cat?.gradient ?? 'from-zinc-500 to-zinc-600',
        )}
      >
        <Icon size={14} className="text-white" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{tool.name}</div>
        <div className="text-xs text-fg-muted truncate">{tool.description}</div>
      </div>
      <div className="shrink-0 flex items-center gap-1.5">
        {tool.badge && (
          <span
            className={cn(
              'text-2xs uppercase tracking-wider font-semibold',
              tool.badge === 'soon' ? 'text-warning' : 'text-accent',
            )}
          >
            {tool.badge}
          </span>
        )}
        <ArrowUpRight size={12} className="text-fg-subtle" />
      </div>
    </button>
  );
}
